import React from 'react';
import { AlertTriangle, X, Mail } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

/**
 * AppleMusicAlreadyLinkedModal
 * 
 * Displayed when a user tries to connect an Apple Music account
 * that is already linked to another Tap In profile.
 * 
 * STRICT POLICY: 1 Apple Music Account = 1 Tap In Profile
 */
const AppleMusicAlreadyLinkedModal = ({ 
  isOpen, 
  onClose, 
  linkedUsername 
}) => {
  const { isDarkMode } = useTheme();
  
  if (!isOpen) return null;
  
  const handleContactSupport = () => {
    // Open email client with pre-filled support request
    const subject = encodeURIComponent('Apple Music Account Dispute');
    const body = encodeURIComponent(
      `Hi Tap In Support,\n\nI believe there is an error with my Apple Music account linkage.\n\nThe system shows my Apple Music account is linked to @${linkedUsername}, but I believe this is incorrect.\n\nPlease help me resolve this issue.\n\nThank you.`
    );
    window.location.href = `mailto:support@tapinsound.com?subject=${subject}&body=${body}`;
  };
  
  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
      onClick={onClose}
    >
      <div 
        className={`relative w-full max-w-md rounded-2xl p-6 ${
          isDarkMode ? 'bg-gray-900' : 'bg-white'
        } shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
        data-testid="apple-music-linked-modal"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${
            isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
          }`}
          data-testid="close-apple-music-modal"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Warning Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
          </div>
        </div>
        
        {/* Title */}
        <h2 className={`text-xl font-bold text-center mb-3 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          Apple Music Already Linked
        </h2>
        
        {/* Message */}
        <div className={`text-center space-y-3 mb-6 ${
          isDarkMode ? 'text-gray-300' : 'text-gray-600'
        }`}>
          <p>
            This Apple Music account is already in use by{' '}
            <span className="font-bold text-purple-500">@{linkedUsername}</span>
          </p>
          <p className="text-sm">
            There can only be <span className="font-semibold">one profile account</span> per Apple Music account.
          </p>
        </div>
        
        {/* Divider */}
        <div className={`border-t mb-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} />
        
        {/* Help Text */}
        <p className={`text-xs text-center mb-4 ${
          isDarkMode ? 'text-gray-400' : 'text-gray-500'
        }`}>
          If you believe this is an error, please contact support.
        </p>
        
        {/* Buttons */}
        <div className="flex flex-col gap-3">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl font-semibold bg-purple-500 text-white hover:bg-purple-600 transition-colors"
            data-testid="ok-button"
          >
            OK
          </button>
          
          <button
            onClick={handleContactSupport}
            className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors ${
              isDarkMode 
                ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            data-testid="contact-support-button"
          >
            <Mail className="w-4 h-4" />
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppleMusicAlreadyLinkedModal;
